import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import { resources } from '../../data/mock';
import { BookOpen, GraduationCap, Tablet, Users, ArrowLeft, ArrowRight, Loader2, Lock } from 'lucide-react';
import { Button } from '../ui/button';

// Import API URL from utility
import { API_URL } from '../../lib/api';

const iconMap = {
  BookOpen: BookOpen,
  GraduationCap: GraduationCap,
  Tablet: Tablet,
  Users: Users
};

const colorMap = {
  coral: 'from-coral-100 to-coral-200 text-coral-600',
  blue: 'from-sky-100 to-sky-200 text-sky-600',
  teal: 'from-teal-100 to-teal-200 text-teal-600',
  gold: 'from-amber-100 to-amber-200 text-amber-600'
};

// Get page count from various formats
const getPageCount = (book) => {
  if (book.pages && Array.isArray(book.pages)) {
    return book.pages.filter(Boolean).length;
  }
  if (book.pageImages && Array.isArray(book.pageImages)) {
    return book.pageImages.filter(Boolean).length;
  }
  if (typeof book.pages === 'number') return book.pages;
  if (typeof book.total_pages === 'number') return book.total_pages;
  return 0;
};

const ResourcesPage = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // Fetch books from API
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const response = await fetch(`${API_URL}/api/books`);
        if (response.ok) {
          const data = await response.json();
          // Sort by created_at descending (newest first)
          const sorted = data.sort((a, b) => {
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateB - dateA;
          });
          setBooks(sorted);
        }
      } catch (err) {
        console.log('Error fetching books:', err);
      }
      setLoading(false);
    };

    fetchBooks();
  }, []);

  const displayedBooks = showAll ? books : books.slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-emerald-50">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-10 right-20 w-32 h-32 bg-sky-200 rounded-full blur-3xl" />
            <div className="absolute bottom-10 left-10 w-40 h-40 bg-coral-200 rounded-full blur-3xl" />
          </div>
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <Link to="/" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-8 group">
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              Back to Home
            </Link>
            
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-sky-100 rounded-2xl">
                <BookOpen className="w-8 h-8 text-sky-600" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-serif font-bold text-slate-800">
                Resources
              </h1>
            </div>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              Explore our collection of faith-building resources designed specifically for children and families.
            </p>
          </div>
        </section>

        {/* Resource Categories */}
        <section className="py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-serif font-bold text-slate-800 mb-8">
              Resource Categories
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {resources.map((resource) => {
                const Icon = iconMap[resource.icon] || BookOpen;
                const colorClass = colorMap[resource.color] || colorMap.coral;
                
                return (
                  <div
                    key={resource.id}
                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-slate-100 group cursor-pointer"
                  >
                    <div className={`w-14 h-14 bg-gradient-to-br ${colorClass} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <h3 className="font-bold text-slate-800 mb-2">{resource.title}</h3>
                    <p className="text-sm text-slate-600">{resource.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Featured Books */}
        <section className="py-12 bg-gradient-to-r from-emerald-50 to-sky-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-serif font-bold text-slate-800">
                Featured Books
              </h2>
              {books.length > 3 && !showAll && (
                <button 
                  onClick={() => setShowAll(true)}
                  className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 group"
                >
                  More
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              )}
            </div>
            
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto" />
                <p className="text-slate-500 mt-2">Loading books...</p>
              </div>
            ) : books.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No books available yet. Check back soon!</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayedBooks.map((book) => {
                  const pageCount = getPageCount(book);
                  const coverImage = book.cover_url || book.cover || (book.pages && book.pages[0]) || null;
                  
                  return (
                    <div
                      key={book.book_id || book.id}
                      className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 group"
                    >
                      <div className="aspect-[3/4] relative overflow-hidden bg-slate-100">
                        {coverImage ? (
                          <img
                            src={coverImage}
                            alt={book.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-300">
                            <BookOpen className="w-16 h-16" />
                          </div>
                        )}
                        
                        {/* Tier badge */}
                        <div className="absolute top-3 right-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            book.tier === 'free' ? 'bg-emerald-100 text-emerald-700' :
                            book.tier === 'basic' ? 'bg-sky-100 text-sky-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {book.tier === 'free' ? 'Free' : book.tier === 'basic' ? 'Adventurer' : 'Champion'}
                          </span>
                        </div>
                        
                        {/* Locked overlay for non-free books */}
                        {book.tier !== 'free' && (
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end justify-center pb-6">
                            <div className="flex items-center gap-2 text-white bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full">
                              <Lock className="w-4 h-4" />
                              <span className="text-sm font-medium">Upgrade to read</span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="p-4">
                        <h3 className="font-bold text-slate-800 mb-1">{book.title}</h3>
                        <p className="text-sm text-slate-500 mb-2">by {book.author || 'Creassion Resources'}</p>
                        
                        {book.description && (
                          <p className="text-sm text-slate-600 line-clamp-2 mb-3">{book.description}</p>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-400">{pageCount} pages</span>
                          {book.age_range && (
                            <span className="text-xs text-slate-400">Ages {book.age_range}</span>
                          )}
                        </div>
                        
                        {/* Sign up CTA */}
                        <Link to="/login" className="block mt-4">
                          <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full">
                            Sign up to read
                          </Button>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {showAll && books.length > 3 && (
              <div className="text-center mt-8">
                <button 
                  onClick={() => setShowAll(false)}
                  className="text-slate-500 hover:text-slate-700 text-sm"
                >
                  Show less
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-coral-500 to-amber-500 rounded-3xl p-8 sm:p-12 text-center text-white">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-4">
                Start Reading Today
              </h2>
              <p className="text-coral-100 mb-8 max-w-2xl mx-auto">
                Sign up for free and begin your faith-building journey with our collection of engaging children's books.
              </p>
              <Link to="/login">
                <Button className="bg-white text-coral-600 hover:bg-coral-50 rounded-full px-8 py-6">
                  <BookOpen className="w-5 h-5 mr-2" />
                  Start Reading Free
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ResourcesPage;
